
import { useApiQuery, useApiMutation, optimisticUpdate } from "@/hooks/use-api"
import { toast } from "@/components/ui/use-toast"

export enum UserType {
  ADMIN = "admin",
  RECEPTION = "reception",
  TRAINER = "trainer",
}

export interface Staff {
  _id?: string
  id?: string
  name: string
  surname: string
  email: string
  phone: string
  status: "active" | "inactive"
  gym: string[] // Array of gym IDs this staff member is assigned to
  userType: UserType
  createdAt: string
  updatedAt?: string
  users?: any[] // Assigned users
}

export interface CreateStaffData {
  name: string
  surname: string
  email: string
  phone: string
  status: "active" | "inactive"
  gym: string[]
  userType: UserType
  password: string
}

export interface UpdateStaffData {
  id?: string
  _id?: string
  name?: string
  surname?: string
  email?: string
  phone?: string
  status?: "active" | "inactive"
  gym?: string[]
  userType?: UserType
}

interface PaginatedStaffResponse {
  results: Staff[]
  total: number
  page: number
  limit: number
  totalPages: number
}

// Get all staff members
export function useStaff(gymId?: string) {
  const query = useApiQuery<PaginatedStaffResponse>(
    ["staff"],
    {
      method: "GET",
      url: "/users?limit=1000",
    },
    {
      enabled: true,
    },
  )

  const data: Staff[] = query.data
    ? Array.isArray(query.data)
      ? (query.data as unknown as Staff[])
      : (query.data.results ?? [])
    : []

  return { ...query, data }
}

// Get a single staff member
export function useStaffMember(id: string) {
  return useApiQuery<Staff>(
    ["staff", id],
    { method: "GET", url: `/users/${id}` },
    {
      enabled: !!id,
    },
  )
}

// Create a staff member
export function useCreateStaff() {
  return useApiMutation<Staff, CreateStaffData>(
    (data) => ({
      method: "POST",
      url: "/users",
      data,
    }),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Staff member created successfully",
        })
      },
    },
  )
}

// Update a staff member
export function useUpdateStaff() {
  return useApiMutation<Staff, UpdateStaffData>(
    (data) => ({
      method: "PUT",
      url: `/users/${data.id || data._id}`,
      data,
    }),
    {
      ...optimisticUpdate({
        queryKey: ["staff", "id"],
        updateFn: (old, variables) => {
          if (!old) return old
          return { ...old, ...variables }
        },
      }),
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Staff member updated successfully",
        })
      },
    },
  )
}

// Delete a staff member
export function useDeleteStaff() {
  return useApiMutation<void, string>(
    (id) => ({
      method: "DELETE",
      url: `/users/${id}`,
    }),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Staff member deleted successfully",
        })
      },
    },
  )
}

// Assign a user to a staff member
export function useAssignUserToStaff() {
  return useApiMutation<void, { staffId: string; userId: string }>(
    (data) => ({
      method: "POST",
      url: `/users/assign-user/${data.staffId}/${data.userId}`,
    }),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "User assigned to staff successfully",
        })
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message || "Failed to assign user to staff",
          variant: "destructive",
        })
      },
    },
  )
}

// Remove a user from a staff member
export function useRemoveUserFromStaff() {
  return useApiMutation<void, { staffId: string; userId: string }>(
    (data) => ({
      method: "DELETE",
      url: `/users/assign-user/${data.staffId}/${data.userId}`,
    }),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "User removed from staff successfully",
        })
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message || "Failed to remove user from staff",
          variant: "destructive",
        })
      },
    },
  )
}
